import {
  SignalingMessage,
  SignalingMessageType,
  UserProps
} from "./SignalingMessage";
import { UserType } from "../UserType";

export const MAX_SDP_LENGTH = 16384;
export const MAX_CANDIDATE_LENGTH = 1024;
export const MAX_USERNAME_LENGTH = 256;
export const MAX_USERLIST_USERS = 128;
export const MAX_MESSAGE_BYTES = 65536;

const KNOWN_TYPES = new Set<string>(Object.values(SignalingMessageType));
const KNOWN_USER_TYPES = new Set<string>(Object.values(UserType));

export class InvalidSignalingMessageError extends Error {
  public constructor(message: string) {
    super(message);
    this.name = "InvalidSignalingMessageError";
  }
}

function isObject(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null;
}

function fail(message: string): never {
  throw new InvalidSignalingMessageError(message);
}

function assertString(value: unknown, field: string): string {
  if (typeof value !== "string") fail(`Expected string for "${field}"`);
  return value;
}

function assertNumber(value: unknown, field: string): number {
  if (typeof value !== "number" || !Number.isFinite(value))
    fail(`Expected number for "${field}"`);
  return value;
}

function assertBoundedString(
  value: unknown,
  field: string,
  max: number
): string {
  const str = assertString(value, field);
  if (str.length > max) fail(`"${field}" exceeds max length ${max}`);
  return str;
}

function assertUser(value: unknown, field: string): UserProps {
  if (!isObject(value)) fail(`Expected object for "${field}"`);
  const username = assertBoundedString(
    value.username,
    `${field}.username`,
    MAX_USERNAME_LENGTH
  );
  const id = assertNumber(value.id, `${field}.id`);
  const type = assertString(value.type, `${field}.type`);
  if (!KNOWN_USER_TYPES.has(type))
    fail(`Unknown user type "${type}" for "${field}.type"`);
  return { username, id, type: type as UserType };
}

function assertEnvelope(raw: unknown): SignalingMessage {
  if (!isObject(raw)) fail("Message is not a non-null object");
  const type = raw.type;
  if (typeof type !== "string" || !KNOWN_TYPES.has(type))
    fail(`Unknown signaling message type "${String(type)}"`);
  const data = raw.data;
  if (!isObject(data)) fail(`"data" must be a non-null object`);
  if (raw.from !== undefined) assertNumber(raw.from, "from");
  if (raw.to !== undefined) assertNumber(raw.to, "to");
  return raw as unknown as SignalingMessage;
}

export function validateSignalingMessage(raw: unknown): SignalingMessage {
  const msg = assertEnvelope(raw);
  const data = msg.data as Record<string, unknown>;

  switch (msg.type) {
    case SignalingMessageType.OFFER:
    case SignalingMessageType.ANSWER: {
      assertBoundedString(data.sdp, "sdp", MAX_SDP_LENGTH);
      assertString(data.room, "room");
      assertNumber(data.datetime, "datetime");
      break;
    }
    case SignalingMessageType.CANDIDATE: {
      assertBoundedString(data.candidate, "candidate", MAX_CANDIDATE_LENGTH);
      assertNumber(data.sdpMLineIndex, "sdpMLineIndex");
      assertString(data.sdpMid, "sdpMid");
      assertString(data.room, "room");
      assertNumber(data.datetime, "datetime");
      break;
    }
    case SignalingMessageType.CONNECT: {
      assertNumber(data.id, "id");
      break;
    }
    case SignalingMessageType.CHANGE_USERNAME: {
      assertString(data.room, "room");
      assertUser(data.user, "user");
      break;
    }
    case SignalingMessageType.USERLIST: {
      assertString(data.room, "room");
      if (!Array.isArray(data.users)) fail(`"users" must be an array`);
      if (data.users.length > MAX_USERLIST_USERS)
        fail(`"users" exceeds max length ${MAX_USERLIST_USERS}`);
      msg.data = {
        room: data.room as string,
        users: (data.users as unknown[]).map((u, i) =>
          assertUser(u, `users[${i}]`)
        )
      };
      break;
    }
    case SignalingMessageType.DISCONNECT: {
      if (!isObject(data.user)) fail(`Expected object for "user"`);
      assertNumber(data.user.id, "user.id");
      assertBoundedString(
        data.user.username,
        "user.username",
        MAX_USERNAME_LENGTH
      );
      assertString(data.room, "room");
      break;
    }
  }

  return msg;
}
